import { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const TILE = 24;

// Helper Web Audio API untuk efek suara gelombang laut / retro wave sweep
const playWaveSound = (type) => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    // 1. Buat White Noise Buffer (sumber dasar suara deru)
    const bufferSize = ctx.sampleRate * 0.5; // Durasi 0.5 detik
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const whiteNoise = ctx.createBufferSource();
    whiteNoise.buffer = noiseBuffer;

    // 2. Filter Low-Pass untuk membuat efek desiran angin / gelombang
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';

    // 3. Gain Node untuk pembentukan envelope volume
    const gainNode = ctx.createGain();

    if (type === 'fill') {
      // Gelombang naik: frekuensi filter menyapu dari rendah ke tinggi (desiran mendekat)
      filter.frequency.setValueAtTime(200, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.4);

      gainNode.gain.setValueAtTime(0.01, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.2);
      gainNode.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.45);
    } else {
      // Gelombang surut (drain): frekuensi menyapu dari tinggi ke rendah (desiran menjauh)
      filter.frequency.setValueAtTime(1000, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.4);

      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    }

    // Hubungkan node audio
    whiteNoise.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(ctx.destination);

    whiteNoise.start();
    whiteNoise.stop(ctx.currentTime + 0.45);
  } catch (e) {
    // Handling autoplay browser policy
  }
};

export default function ThemeTransitionOverlay() {
  const { themeAnim, isDark } = useTheme();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const fillColor = isDark ? '#060B14' : '#F8FAFC';
  const accentColor = isDark ? '#2563EB' : '#3B82F6';

  useEffect(() => {
    if (!themeAnim) return;

    // Bunyikan suara gelombang sesuai fase transisi
    if (themeAnim === 'start' || themeAnim === 'fill') {
      playWaveSound('fill');
    } else if (themeAnim === 'drain') {
      playWaveSound('drain');
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;

    const cols = Math.ceil(W / TILE);
    const rows = Math.ceil(H / TILE);

    // Pre-calculate ubin dengan pola diagonal wave (r + c)
    const tiles = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const isEven = (r + c) % 2 === 0;
        tiles.push({
          r,
          c,
          order: (r + c) + (isEven ? 0 : 0.5)
        });
      }
    }

    tiles.sort((a, b) => a.order - b.order);

    const totalTiles = tiles.length;
    let startTime = null;
    const DURATION = 420;

    function drawFrame(ts) {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / DURATION, 1);

      const easedProgress = 1 - (1 - progress) * (1 - progress);
      const activeCount = Math.floor(easedProgress * totalTiles);

      ctx.clearRect(0, 0, W, H);

      if (themeAnim === 'start' || themeAnim === 'fill') {
        for (let i = 0; i < activeCount; i++) {
          const { r, c } = tiles[i];
          const x = c * TILE;
          const y = r * TILE;

          if (i >= activeCount - 25) {
            ctx.fillStyle = accentColor;
          } else {
            ctx.fillStyle = fillColor;
          }
          ctx.fillRect(x, y, TILE, TILE);
        }

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(drawFrame);
        }

      } else if (themeAnim === 'drain') {
        ctx.fillStyle = fillColor;
        ctx.fillRect(0, 0, W, H);

        for (let i = 0; i < activeCount; i++) {
          const { r, c } = tiles[i];
          const x = c * TILE;
          const y = r * TILE;
          ctx.clearRect(x, y, TILE, TILE);
        }

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(drawFrame);
        } else {
          ctx.clearRect(0, 0, W, H);
        }
      }
    }

    rafRef.current = requestAnimationFrame(drawFrame);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [themeAnim, fillColor, accentColor]);

  if (!themeAnim) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        pointerEvents: 'none',
        imageRendering: 'pixelated',
      }}
    />
  );
}